// Copied from flobi-flow src/lib/videoTasks.js (518142c9, 13 Oct) so Director's Generate drawer reads a model exactly as Flow's
// prompt bar does. Keep in step with Flow: change it there first, then copy it here.
/**
 * What a VIDEO node does (Tarek, 2026-09-26 — "keep the same pattern as the
 * playground"):
 *
 *   - The Video node makes a video in one of two MODES, a band at its top:
 *       Reference  words → video, plus any references the model takes
 *                  (images, videos, audio)
 *       Keyframe   a first frame → video; a last frame too when the model
 *                  has one (optional). A first frame alone is image → video.
 *   - Edit video, Motion transfer, Talking video and Translate video are
 *     NODES of their own: a video node with a fixed task (the library adds
 *     them; nothing on the node switches it).
 *
 * The mode / task decides:
 *   - which models are offered (`modelServes` — the model list is filtered),
 *   - which named inputs the node has (`cardsFor` — "First frame", "Face"…),
 *     each on the real model input it feeds, so wires, cApps and the agent
 *     read the classic port ids and data keys,
 *   - the catalogue's mode switches (`enable_two_frame`, `enable_reference`),
 *     which users never see: `applyVideoTask` sets them on the run, and
 *     `taskRules` drops the catalogue rules keyed on them so no wire is
 *     hidden by a switch the node doesn't show.
 *
 * What a model takes is DECLARED by ai-back (`videoInputs` on the catalogue
 * row, from the provider's docs and the handler that calls it — 2026-10-01);
 * a model without a declaration is read from its inputs, as before.
 *
 * Stored on the node as `data.videoTask`: "reference" | "keyframe" on the
 * Video node, "edit" | "motion" | "talking" | "translate" on the tools. Boards from before
 * the modes say "generate" / "references" — `taskOf` reads those as today's.
 * One node type (`video`) — every reader of video nodes keeps working.
 * A model's DECLARED tasks (ai-back `tasks`, lib/modelTasks — "video.edit"
 * …) decide over every rule below: what it serves, its capabilities, whether
 * it's a video model at all. A model with no tasks (every one from before the
 * field) is read by those rules, unchanged.
 *
 * Pure: no React, no store.
 */
import { declaredIn } from "./modelTasks.js";
import { formatCredits } from "@flobi/asset-library/credits";

// `off`: a left-out wire's reason, in the prompt bar's words (Frames | References, 2026-10-01).
export const VIDEO_MODES = [
  { id: "reference", name: "Reference", noun: "This video", off: "Not used with references", verb: "make videos from words", blurb: "From words, and any references you add" },
  { id: "keyframe", name: "Keyframe", noun: "This video", off: "Not used with frames", verb: "make videos from a frame", blurb: "From a first frame — and a last one, if the model takes it" },
];
export const VIDEO_TOOLS = [
  { id: "edit", name: "Edit video", noun: "Edit video", verb: "edit videos", blurb: "Change a video you have" },
  { id: "motion", name: "Motion transfer", noun: "Motion transfer", verb: "transfer motion", blurb: "Move a character the way someone moves in a video" },
  { id: "talking", name: "Talking video", noun: "Talking video", verb: "make talking videos", blurb: "A face that speaks a voice" },
  { id: "translate", name: "Translate video", noun: "Translate video", verb: "translate videos", blurb: "The same video, speaking another language" },
];
export const VIDEO_TASKS = [...VIDEO_MODES, ...VIDEO_TOOLS];
export const DEFAULT_TASK = "reference";
/**
 * A mode switch asked of a node from outside it (the Composer prompt bar):
 * window event, detail `{ id, task, handled }`. A mounted node switches
 * itself (useVideoState — its wires move with the mode) and sets `handled`;
 * unhandled (the node isn't drawn live), the caller writes the mode itself.
 */
export const VIDEO_SET_TASK_EVENT = "flobi-video-set-task";
const TASK_IDS = new Set(VIDEO_TASKS.map((t) => t.id));
const TOOL_IDS = new Set(VIDEO_TOOLS.map((t) => t.id));
/** A mode or task of today ("generate" / "references", the words from before the modes, aren't). */
export const isTaskId = (task) => TASK_IDS.has(task);
/** Edit video, Motion transfer, Talking video — a node of its own, its job fixed. */
export const isToolTask = (task) => TOOL_IDS.has(task);
export const taskById = (id) => VIDEO_TASKS.find((t) => t.id === id) || VIDEO_TASKS[0];
/** "Not used with frames", "Edit video doesn’t use it" — a wire the mode / task leaves out ("Not used", on hover). */
export const offText = (task) => taskById(task).off || `${taskById(task).noun} doesn’t use it`;
/**
 * "Kling Avatar doesn’t make videos from words — pick a model": the node's
 * model doesn't do its mode / task. No model to name (none set, or one the
 * catalogue doesn't have): "Pick a model to translate videos".
 */
export const mismatchText = (modelName, task) => (modelName ? `${modelName} doesn’t ${taskById(task).verb} — pick a model` : `Pick a model to ${taskById(task).verb}`);

export const REFS = {
  prompt: "prompt",
  images: "images_urls",
  first: "first_frame_image_url",
  last: "last_frame_image_url",
  video: "videoUrl",
  audio: "audioUrl",
  language: "language",
  twoFrame: "enable_two_frame",
  reference: "enable_reference",
};
/** The catalogue's mode switches — the mode sets them; never shown. */
export const TASK_SWITCHES = new Set([REFS.twoFrame, REFS.reference]);
const MEDIA_REFS = [REFS.images, REFS.first, REFS.last, REFS.video, REFS.audio];

/** Models that are one-step tools (upscale, captions…), not a video task. They live in the Tools menu. */
const TOOLS = new Set([
  // Segment video's AI (its own node — never an Edit video model).
  "sam-3-video",
  "bria/video/background-removal",
  "mirelo-ai/sfx-v1.5/video-to-video",
  "tiktok-short-captions",
  "seed-vr-upscaler",
  "mmaudio-v2",
  "fal-ai/topaz/upscale/video",
  "clarityai/crystal-video-upscaler",
  "bria/video/increase-resolution",
  "fal-ai/flashvsr/upscale/video",
]);
const MOTION = new Set(["kling-v3-motion-control", "kling-v3-motion-control-pro", "wan/v2.2-14b/animate/move", "wan/v2.2-14b/animate/replace", "marey/pose-transfer", "marey/motion-transfer"]);
const TALKING = new Set(["kling-ai-avatar-v2", "kling-ai-avatar-v2-kie", "omni-human", "omni-human-v1.5", "fal/sync-lipsync-v2-pro"]);
const EDIT = new Set(["runway-aleph", "runway-aleph-2", "luma-reframe-video", "luma-modify"]);
/** Dub a video into another language (its own node since 2026-09-27; still in the Tools menu too). */
const TRANSLATE = new Set(["heygen-video-translate"]);
/** Take several images but not as references (veo3 reads two images as first and last frames on kie, and drops them on fal). */
const NO_REFERENCES = new Set(["veo3"]);

const slug = (m) => m?.nameId || m?.name || "";
const entriesOf = (m, ref) => {
  const all = m?.modelInputs;
  if (!all) return [];
  if (Array.isArray(all)) return all.filter((e) => e?.reference === ref);
  return all[ref] || [];
};
/** Does the model take this input (at any level)? */
export const has = (m, ref) => entriesOf(m, ref).length > 0;
/** How many of this input the model takes (0 = none). */
export function capOf(m, ref) {
  let max = 0;
  for (const e of entriesOf(m, ref)) {
    const d = e?.modelInputData || e || {};
    const n = Number(d.nbInputs) || Number(d.mediaLimit) || 1;
    if (n > max) max = n;
  }
  return max;
}
export const requires = (m, ref) => entriesOf(m, ref).some((e) => (e?.modelInputData || e)?.isRequired);

/**
 * A video model's DECLARED inputs (ai-back AIModels.videoInputs, written from
 * the provider's docs and the handler that calls it): { text, firstFrame
 * "none"|"optional"|"required", lastFrame, refImages, refImagesMin, refVideos,
 * refAudio, framesWithRefs } — or null, and the model's inputs are read
 * instead. `images_urls` is the first frame on some models and the references
 * on others; only the declaration says which.
 */
export function declaredOf(m) {
  const d = m?.videoInputs;
  return d && typeof d === "object" && !Array.isArray(d) ? d : null;
}
const countOf = (n) => (Number.isFinite(Number(n)) ? Math.max(0, Math.floor(Number(n))) : 0);
const declaresRefs = (d) => countOf(d.refImages) + countOf(d.refVideos) + countOf(d.refAudio) > 0;
const startsFromFrame = (d) => d.firstFrame === "optional" || d.firstFrame === "required";

/**
 * What a video model can do, best first ([] for tools) — its capabilities,
 * not the node's modes: "generate" (a video from words or from a frame),
 * "references" (composes from reference images / videos / audio), "edit",
 * "motion", "talking", "translate". The node's modes read these (`modelServes`).
 */
export function tasksOfModel(m) {
  const id = slug(m);
  if (!id) return [];
  // Declared (ai-back tasks): its video tasks as capabilities — none of them
  // video ones = no video task at all (like a tool).
  const said = declaredIn(m, "video");
  if (said) {
    const out = [];
    if (said.includes("reference") || said.includes("keyframe")) out.push("generate");
    if (said.includes("reference") && takesReferences(m)) out.push("references");
    for (const t of TOOL_IDS) if (said.includes(t)) out.push(t);
    return out;
  }
  if (TOOLS.has(id)) return [];
  if (TRANSLATE.has(id)) return ["translate"];
  if (MOTION.has(id)) return ["motion"];
  if (TALKING.has(id)) return ["talking"];
  if (EDIT.has(id)) return ["edit"];
  // Declared (ai-back videoInputs): what it says, nothing guessed.
  const d = declaredOf(m);
  if (d) {
    const out = [];
    if (d.text || startsFromFrame(d)) out.push("generate");
    if (declaresRefs(d)) out.push("references");
    return out;
  }
  // A model we don't know yet, by what it needs.
  const needsVideo = requires(m, REFS.video);
  const needsAudio = requires(m, REFS.audio);
  if (needsAudio && (needsVideo || requires(m, REFS.images))) return ["talking"];
  if (needsVideo && requires(m, REFS.images) && !has(m, REFS.prompt)) return ["motion"];
  if (needsVideo) return ["edit"];
  if (!has(m, REFS.prompt)) return [];
  const out = ["generate"];
  // Several references, or a video / audio one: it composes from references too.
  if (takesReferences(m)) out.push("references");
  return out;
}

/** Does it compose from references (several images, or a video / audio one)? Its declared inputs say, else its inputs. */
function takesReferences(m) {
  const d = declaredOf(m);
  if (d) return declaresRefs(d);
  return !NO_REFERENCES.has(slug(m)) && (capOf(m, REFS.images) >= 2 || has(m, REFS.video) || has(m, REFS.audio));
}

/**
 * Does model `m` do this mode / task?
 *   Reference — it composes from references, or makes a video from words
 *               alone (needs no picture)
 *   Keyframe  — it makes videos and takes a picture to start from
 *   a tool    — it does that job
 */
export function modelServes(m, task) {
  // Declared tasks decide (ai-back tasks): "video.edit" = Edit video, and so on.
  const said = declaredIn(m, "video");
  if (said) return said.includes(task);
  const d = declaredOf(m);
  if (d && !isToolTask(task)) {
    if (task === "reference") return !!d.text || declaresRefs(d);
    if (task === "keyframe") return startsFromFrame(d);
  }
  const can = tasksOfModel(m);
  if (task === "reference") return can.includes("references") || (can.includes("generate") && !requires(m, REFS.first) && !requires(m, REFS.images));
  if (task === "keyframe") return can.includes("generate") && (has(m, REFS.first) || has(m, REFS.images));
  return can.includes(task);
}

/**
 * The mode a node should show with model `m` (Dana, 30 Sep: "make the
 * reference or keyframe smart depending on model — if he doesn't accept one
 * of them switch to the other directly, else stay in place"): Reference on a
 * model that takes no references but starts from a picture → Keyframe (the
 * picture becomes the first frame instead of an unused reference); Keyframe
 * on a model that can't start from a picture → Reference. Anything else —
 * a model that does both, a tool's task, no model — stays. Asked ONCE, when
 * the model lands or a dropped wire has nowhere to go; never enforced, so a
 * mode you then pick by hand stays.
 */
/**
 * A model that takes no references but starts from a frame (FLUX 3, Grok 1.5
 * Lite…) has ONE mode: Keyframe, its first frame optional when it also makes
 * videos from words — no frame = from words (Tarek, 2026-10-03: "for models
 * that don't support reference, no need to show this"). A reference picture
 * wired in becomes its first frame (cardsFor keyframe `also`).
 */
export function framesOnly(m) {
  if (!m) return false;
  const r = sideRules(m);
  return r.keyframe.ok && !r.reference.refs;
}

export function modeForModel(task, m) {
  if (!m) return task;
  if (task === "reference" && (framesOnly(m) || (!tasksOfModel(m).includes("references") && modelServes(m, "keyframe")))) return "keyframe";
  if (task === "keyframe" && !modelServes(m, "keyframe") && modelServes(m, "reference")) return "reference";
  return task;
}

/**
 * What a model can do on each side of a Video node — the
 * node's mode band and the prompt bar's Frames | References read this one
 * answer (Tarek, 2026-10-01: "some models don't support first frame last
 * frame… some don't have references — tell users"):
 *   keyframe   { ok, model, why, last, lastWhy } — `ok` false: no row starts
 *              from a picture; `last`: it has a last frame (else "Optional"
 *              doesn't apply — "Not supported")
 *   reference  { ok, model, refs, why } — `ok` false: every row needs a
 *              picture (it can't make a video from words); `refs` false: it
 *              takes no reference pictures — words only
 * `model`: the model, when it does that side (else null). Read from its
 * declared inputs (declaredOf) when ai-back has them.
 */
export function sideRules(m, name = "This model") {
  if (!m) return { keyframe: { ok: true, model: null, why: "", last: true, lastWhy: "" }, reference: { ok: true, model: null, refs: true, why: "" } };
  const doing = (task) => (modelServes(m, task) ? m : null);
  const frames = doing("keyframe");
  const words = doing("reference");
  const refs = !!words && cardsFor("reference", words).length > 0;
  return {
    keyframe: { ok: !!frames, model: frames, why: frames ? "" : `${name} doesn’t start from a frame`, last: !!frames && has(frames, REFS.last), lastWhy: `${name} has no last frame` },
    reference: { ok: !!words, model: words, refs, why: !words ? `${name} needs a first frame` : refs ? "" : `${name} takes no references — words only` },
  };
}

/** Who a mode / task starts on when the node's model doesn't do it — the first of these that exists, else the first that does it. */
const PREFERRED = {
  reference: ["seedance-2.5", "seedance-2", "veo3_fast", "kling-v3-pro"],
  keyframe: ["seedance-2.5", "kling-v3-pro", "seedance-2", "veo3_fast"],
  edit: ["runway-aleph-2", "runway-aleph"],
  motion: ["kling-v3-motion-control", "kling-v3-motion-control-pro", "wan/v2.2-14b/animate/move"],
  talking: ["kling-ai-avatar-v2", "omni-human-v1.5", "fal/sync-lipsync-v2-pro"],
  translate: ["heygen-video-translate"],
};
/** Words from before the modes (alone, with no wires to read — taskOf reads those). */
const OLD_WORDS = { generate: "reference", references: "reference" };
export function startingModel(task, models) {
  const t = OLD_WORDS[task] || task;
  const doing = (models || []).filter((m) => m?.type === "VIDEO" && m.healthy !== false && modelServes(m, t));
  for (const id of PREFERRED[t] || []) {
    const hit = doing.find((m) => slug(m) === id);
    if (hit) return slug(hit);
  }
  return doing.length ? slug(doing[0]) : null;
}
export const isVideoTool = (m) => TOOLS.has(slug(m));
/** A model whose declared tasks (ai-back tasks) hold no video one — no video picker lists it, whatever its type. False for a model that declares none. */
export const declaresNoVideoTask = (m) => declaredIn(m, "video")?.length === 0;

/** The modes and tasks at least one (healthy) video model does. */
export function availableTasks(models) {
  return VIDEO_TASKS.filter((t) => startingModel(t.id, models));
}

/**
 * The node's mode / task:
 *   - what it says ("reference" | "keyframe" | a tool);
 *   - words from before the modes, read as today's: "references" →
 *     Reference; "generate" → Keyframe when a picture is wired, else
 *     Reference;
 *   - none (made before tasks, or by the agent): a tool's model → that tool;
 *     else its wires (several images, a video or audio → Reference; one
 *     picture or a frame → Keyframe); else Reference.
 * A word from before, or none, never lands on a mode the model can't do
 * when the other mode fits it.
 * `counts`: the wires the model uses, by input ({ images_urls: 2, videoUrl: 1 }), optional.
 */
export function taskOf(data, model, counts = null) {
  const t = data?.videoTask;
  // A saved Reference on a model with no references reads as Keyframe (framesOnly).
  if (TASK_IDS.has(t)) return t === "reference" && framesOnly(model) ? "keyframe" : t;
  const n = (ref) => (counts && counts[ref]) || 0;
  const frames = n(REFS.first) + n(REFS.last) > 0;
  let mode;
  if (t === "references") mode = "reference";
  else if (t === "generate") mode = frames || n(REFS.images) > 0 ? "keyframe" : "reference";
  else {
    // (a model that also makes videos — only a declared one can — keeps a mode)
    const can = tasksOfModel(model);
    const tool = can.includes("generate") ? null : can.find((x) => TOOL_IDS.has(x));
    if (tool) return tool;
    const refs = n(REFS.images) >= 2 || n(REFS.video) > 0 || n(REFS.audio) > 0;
    mode = !refs && (frames || n(REFS.images) === 1) ? "keyframe" : "reference";
  }
  if (mode === "reference" && framesOnly(model)) return "keyframe";
  if (model && !modelServes(model, mode)) {
    const other = mode === "keyframe" ? "reference" : "keyframe";
    if (modelServes(model, other)) return other;
  }
  return mode;
}

/**
 * The node's media inputs for a mode / task and model, as cards:
 *   roles — named slots, one row each ("First frame", "Last frame")
 *   list  — numbered slots up to the model's cap ("Image 1, 2…")
 * A role or list the model doesn't take is left out; an empty card too.
 * Every row names the real model input (`ref`) and slot it feeds; `optional`
 * marks a row the node says it can go without ("Optional"). A role may say
 * what goes in (`hint` — under its name on an empty tile, the node's and the
 * prompt bar's; also its card's ⓘ) and what to do while it's missing
 * (`need` — "Add a picture of the character", in place of "X is needed").
 *   keyframe  — "Keyframes": First frame (the model's first-frame input, else
 *               its image input), Last frame (only on a model that has one)
 *   reference — the model's reference lists; none on a model that reads
 *               words only (or reads its pictures as frames)
 * ("generate" / "references", the words from before the modes, give the
 * same cards as Keyframe / every reference list.)
 */
export function cardsFor(task, m) {
  const role = (key, label, ref, type, extra = {}) => (ref && has(m, ref) ? { key, label, ref, slot: 0, type, required: requires(m, ref), ...extra } : null);
  // Declared: a reference list only where the model takes that kind, up to
  // its declared count (and its fewest, `min`).
  const d = declaredOf(m);
  const declaredCap = { [REFS.images]: d ? countOf(d.refImages) : null, [REFS.video]: d ? countOf(d.refVideos) : null, [REFS.audio]: d ? countOf(d.refAudio) : null };
  const list = (key, title, noun, ref, type, info) => {
    if (!has(m, ref)) return null;
    const cap = declaredCap[ref] ?? capOf(m, ref);
    if (!cap) return null;
    const min = ref === REFS.images && d ? countOf(d.refImagesMin) : 0;
    return { key, title, info, kind: "list", ref, type, noun, cap, required: d ? false : requires(m, ref), ...(min > 1 ? { min } : {}) };
  };
  const roles = (key, title, info, rows) => {
    const r = rows.filter(Boolean);
    return r.length ? { key, title, info, kind: "roles", roles: r } : null;
  };
  const referenceLists = () => [
    list("images", "Reference images", "Image", REFS.images, "image", "Characters, objects, places or a style. Type @ in your description to mention one."),
    list("videos", "Reference videos", "Video", REFS.video, "video", "Motion, camera moves or a scene to follow."),
    // Audio on its own is refused (seedance): it needs an image or a video beside it.
    has(m, REFS.audio) ? { ...list("audio", "Reference audio", "Audio", REFS.audio, "audio", "A voice, music or sounds. Needs an image or a video too."), needsAny: ["images", "videos"] } : null,
  ];
  let cards = [];
  if (task === "keyframe" || task === "generate") {
    const withLast = has(m, REFS.last) && (!d || !!d.lastFrame);
    cards = [
      roles("frames", "Keyframes", withLast ? "First frame: where the clip starts. Last frame: where it ends — optional." : "Where the clip starts. This model has no last frame.", [
        // The first frame: the model's own first-frame input, else its image
        // input — and a picture already on images_urls slot 0 (a board from
        // before the modes, an agent wire, Reference image 1) counts as it
        // too (`also`).
        role("first", "First frame", has(m, REFS.first) ? REFS.first : REFS.images, "image", {
          // Some models need a picture to start from, whichever input carries it.
          required: d ? d.firstFrame === "required" : requires(m, REFS.first) || requires(m, REFS.images),
          ...(has(m, REFS.first) && has(m, REFS.images) ? { also: { ref: REFS.images, slot: 0 } } : {}),
        }),
        withLast ? role("last", "Last frame", REFS.last, "image", { required: false, optional: true, needs: "first" }) : null,
      ]),
    ];
  } else if (task === "references" || (task === "reference" && tasksOfModel(m).includes("references"))) {
    // (a model that takes references; declared ones only those it declares)
    cards = referenceLists();
  } else if (task === "edit") {
    // A model that also MAKES videos declares how many pictures its edit
    // takes (`editRefImages`, ai-back videoInputs — 3 Oct 2026): Wan 2.7 makes
    // a video from up to five references but edits with one; its declared
    // `refImages` is the Video node's.
    const editCap = d && d.editRefImages != null ? countOf(d.editRefImages) : null;
    const refList = () => {
      const l = list("refs", "Reference images", "Image", REFS.images, "image", "Optional. How the change should look.");
      return l && editCap ? { ...l, cap: editCap } : l;
    };
    cards = [
      // The video is the whole job: always needed, whatever the catalogue row
      // says — a model that also makes videos (Wan 2.7, HappyHorse 1.0) has it
      // optional, and an Edit node must never run as words → video.
      roles("source", "Video", "The video to change.", [role("video", "Video", REFS.video, "video", { required: true })]),
      editCap === 0
        ? null
        : (editCap ?? capOf(m, REFS.images)) > 1
        ? refList()
        : // Optional whatever the catalogue says: an edit can be words alone (runway-aleph's handler takes no image).
          roles("ref", "Reference", "Optional. How the change should look.", [role("image", "Reference", REFS.images, "image", { required: false })]),
    ];
  } else if (task === "motion") {
    // Character + Moves (Tarek, 3 Oct 2026). The picture is the model's image
    // input, or its first frame on a model that takes it there (DreamActor 2).
    // The Moves role keeps its key "motion" — wires and data read it.
    const MOTION_ROLES = {
      character: { hint: "A picture of them", need: "Add a picture of the character" },
      motion: { hint: "A video to copy", need: "Add a video of the moves" },
    };
    const picture = has(m, REFS.images) ? REFS.images : REFS.first;
    cards = [
      roles("character", "Character", MOTION_ROLES.character.hint, [role("character", "Character", picture, "image", MOTION_ROLES.character)]),
      roles("motion", "Moves", MOTION_ROLES.motion.hint, [role("motion", "Moves", REFS.video, "video", MOTION_ROLES.motion)]),
    ];
  } else if (task === "talking") {
    const face = has(m, REFS.images) ? role("face", "Face", REFS.images, "image") : role("face", "Face", REFS.video, "video");
    cards = [
      roles("face", "Face", face?.type === "video" ? "A video of the person to make speak." : "A clear photo of the person to make speak.", [face]),
      roles("voice", "Voice", "What they say — the video lasts as long as the audio.", [role("voice", "Voice", REFS.audio, "audio")]),
    ];
  } else if (task === "translate") {
    // The language is a setting of the model's own (catalogue values) — kept
    // on the node by `coreSettingsOf`, not a card. The video is the whole job:
    // always there and always needed, whatever the catalogue row says or
    // before the model is read (a Translate node never runs without one).
    cards = [
      {
        key: "source",
        title: "Video",
        info: "Someone speaking — their voice and lips follow the new language.",
        kind: "roles",
        roles: [{ key: "video", label: "Video", ref: REFS.video, slot: 0, type: "video", required: true }],
      },
    ];
  }
  const out = cards.filter(Boolean);
  // A tool's role it can go without says so (an edit's Reference).
  if (TOOL_IDS.has(task)) for (const c of out) if (c.kind === "roles") c.roles = c.roles.map((r) => (r.required ? r : { ...r, optional: true }));
  return out;
}

/** Every model input the cards cover (settings rows skip these). */
export function mediaRefsOf(cards) {
  const out = new Set();
  for (const c of cards) {
    if (c.kind === "list") out.add(c.ref);
    else for (const r of c.roles) out.add(r.ref);
  }
  return out;
}

/**
 * Settings a task keeps ON the node — the thing the node is for (Tarek: "a
 * node's core choice stays on the node"): the language a video is translated
 * into. Always a row at L3 (never behind "+ Add property"), its own chip at
 * L2, no expose dot in the inspector. Only the ones the model has.
 */
const CORE_SETTINGS = { translate: [REFS.language] };
export function coreSettingsOf(task, m) {
  return (CORE_SETTINGS[task] || []).filter((ref) => has(m, ref));
}

/** The catalogue's values for an input (a list setting's options), in order. */
export function valuesOf(m, ref) {
  const out = [];
  for (const e of entriesOf(m, ref)) for (const v of (e?.modelInputData || e || {}).values || []) if (!out.includes(v)) out.push(v);
  return out;
}

/**
 * Other names for the languages a translate model knows: ISO codes (a wired
 * list of "es", "fr"…) and the names people use for the catalogue's own
 * ("Chinese" is "Mandarin" there — the Tools menu used to send "Chinese").
 */
const LANGUAGE_ALIASES = {
  en: "English", es: "Spanish", fr: "French", de: "German", it: "Italian", pt: "Portuguese", nl: "Dutch", ru: "Russian", ja: "Japanese", ko: "Korean",
  zh: "Mandarin", ar: "Arabic", hi: "Hindi", tr: "Turkish", pl: "Polish", sv: "Swedish", da: "Danish", fi: "Finnish", no: "Norwegian", nb: "Norwegian",
  cs: "Czech", ro: "Romanian", el: "Greek", id: "Indonesian", ms: "Malay", th: "Thai", vi: "Vietnamese", fil: "Filipino", tl: "Filipino", uk: "Ukrainian",
  bn: "Bengali", ta: "Tamil",
  chinese: "Mandarin", "mandarin chinese": "Mandarin", "chinese (mandarin)": "Mandarin", "simplified chinese": "Mandarin", tagalog: "Filipino",
  "bahasa indonesia": "Indonesian", "bahasa melayu": "Malay", "brazilian portuguese": "Portuguese", "european portuguese": "Portuguese",
};

/**
 * A language as the model names it (`values` = its catalogue list). A value
 * picked from the list passes as it is; a WIRED text (" spanish", "es",
 * "Chinese") is matched without case, then through LANGUAGE_ALIASES. No
 * match: the text as given, trimmed — the provider says why it can't.
 */
export function languageOf(value, values) {
  if (typeof value !== "string") return value;
  const t = value.trim();
  if (!t || !values?.length) return t;
  const low = t.toLowerCase();
  const hit = values.find((v) => String(v).toLowerCase() === low);
  if (hit) return hit;
  const alias = LANGUAGE_ALIASES[low];
  return alias && values.includes(alias) ? alias : t;
}

/** The kinds of file the cards take ("image" | "video" | "audio"), in card order. */
export function kindsOfCards(cards) {
  const out = [];
  for (const c of cards || []) {
    for (const t of c.kind === "list" ? [c.type] : c.roles.map((r) => r.type)) if (!out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * What a TOOL node (Edit video, Motion transfer, Talking video, Translate
 * video) takes on a wire, whatever model it's on: the kinds of file any model
 * that does the job has a card for, plus "text" where one of them takes words
 * (a prompt, or a core setting such as the language). The canvas refuses the
 * rest (lib/inputRouting) — a Translate video node takes a video, never an
 * image or audio. → Set of "image" | "video" | "audio" | "text", or null when
 * `task` isn't a tool or no model is known yet (don't guess). Translate's
 * video card needs no model, so it answers before the catalogue is read.
 */
export function toolTakes(task, models) {
  if (!TOOL_IDS.has(task)) return null;
  const out = new Set();
  let known = false;
  for (const m of models || []) {
    if (m?.type !== "VIDEO" || m.healthy === false || !modelServes(m, task)) continue;
    known = true;
    for (const k of kindsOfCards(cardsFor(task, m))) out.add(k);
    if (has(m, REFS.prompt) || coreSettingsOf(task, m).length) out.add("text");
  }
  if (!known) {
    if (task !== "translate") return null;
    for (const k of kindsOfCards(cardsFor(task, null))) out.add(k);
    out.add("text");
  }
  return out;
}

/**
 * Where each wire into the node shows, for the cards — the ONE placing
 * every surface uses (the card's rows, L2's tiles, "Not used", the stand-in,
 * what's still needed, the prompt bar):
 *   roles — role key → its wire (its own ref + slot, else its `also`)
 *   lists — card key → its wires, by slot, up to the cap
 *   off   — every other media wire (another mode's input, a slot past a
 *           role or a cap), by ref then slot
 * `wires`: [{ ref, slot, … }] — media wires only, the model uses them.
 */
export function placeWires(cards, wires) {
  const roles = new Map();
  const lists = new Map();
  const used = new Set();
  const at = (ref, slot) => wires.find((w) => !used.has(w) && w.ref === ref && (w.slot || 0) === slot);
  for (const c of cards) {
    if (c.kind === "list") continue;
    for (const r of c.roles) {
      const w = at(r.ref, r.slot) || (r.also ? at(r.also.ref, r.also.slot) : null);
      if (w) {
        used.add(w);
        roles.set(r.key, w);
      }
    }
  }
  for (const c of cards) {
    if (c.kind !== "list") continue;
    const got = wires.filter((w) => !used.has(w) && w.ref === c.ref && (w.slot || 0) < c.cap).sort((a, b) => (a.slot || 0) - (b.slot || 0));
    got.forEach((w) => used.add(w));
    lists.set(c.key, got);
  }
  const off = wires.filter((w) => !used.has(w)).sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : (a.slot || 0) - (b.slot || 0)));
  return { roles, lists, off };
}

/**
 * L2's tiles, in order — what the composer well shows under the band (and
 * the stand-in counts, videoLayout):
 *   wired   — a wired input: its picture, named ("First frame", "Image 2")
 *   role    — a named input with nothing wired: a dashed tile, a press adds a
 *             file (`hint`, when the role has one: what goes in, under its name)
 *   more    — a tool's list with room: a dashed tile for the next one
 *   element — Reference: a Style / Character from your elements
 *   add     — Reference: files onto the next free references
 * Reference shows its wired references then Style · Character (while the
 * model's reference images have room — an element's pictures land there) and
 * Add (while any of its references has room) — nothing on a model that reads
 * words only; once it's full, only what's wired (the image node's rule).
 * `placed` = placeWires(cards, …).
 * → [{ key, kind, label, type?, ref?, slot?, cap?, wire?, elementType?, kinds? }]
 */
export function l2Tiles(task, cards, placed) {
  const out = [];
  const wired = (c) => {
    for (const w of placed.lists.get(c.key) || []) out.push({ key: `${c.key}:${w.slot || 0}`, kind: "wired", label: `${c.noun} ${(w.slot || 0) + 1}`, type: c.type, wire: w });
  };
  const room = (c) => (placed.lists.get(c.key) || []).length < c.cap;
  if (task === "reference" || task === "references") {
    // Add, Character, Style, then the references in (Tarek, 2026-10-04) — every References row.
    const open = cards.filter((c) => c.kind === "list" && room(c));
    if (open.length) out.push({ key: "add", kind: "add", label: "Add", kinds: kindsOfCards(open) });
    if (cards.some((c) => c.kind === "list" && c.type === "image" && room(c))) {
      out.push({ key: "character", kind: "element", label: "Character", elementType: "character" });
      out.push({ key: "style", kind: "element", label: "Style", elementType: "style" });
    }
    for (const c of cards) if (c.kind === "list") wired(c);
    return out;
  }
  for (const c of cards) {
    if (c.kind === "list") {
      wired(c);
      if ((placed.lists.get(c.key) || []).length < c.cap) out.push({ key: `${c.key}:more`, kind: "more", label: c.noun, ref: c.ref, type: c.type, cap: c.cap });
      continue;
    }
    for (const r of c.roles) {
      const w = placed.roles.get(r.key);
      out.push(w ? { key: r.key, kind: "wired", label: r.label, type: r.type, wire: w } : { key: r.key, kind: "role", label: r.label, ref: r.ref, slot: r.slot, type: r.type, ...(r.hint ? { hint: r.hint } : {}) });
    }
  }
  return out;
}

/**
 * L2's "References n/cap" (Reference mode): the references wired in, and
 * how many the model takes in all — every reference list (images, videos,
 * audio) of the mode's cards. → { used, cap }; cap 0 = no references.
 */
export function refsCount(cards, placed) {
  let used = 0;
  let cap = 0;
  for (const c of cards || []) {
    if (c.kind !== "list") continue;
    used += (placed?.lists?.get(c.key) || []).length;
    cap += c.cap;
  }
  return { used, cap };
}

/** "ref|slot" — an input slot, as `freeInputs` reads the ones taken. */
export const slotKey = (ref, slot) => `${ref}|${slot || 0}`;

/**
 * The cards' inputs no wire fills, in card order — where a file or a wire
 * you drop goes. `placed` = placeWires(cards, …); `taken` (optional): a Set
 * of slotKey()s any wire holds (a wire the model leaves out keeps its slot —
 * it comes back there on a roomier model). → [{ ref, slot, type }]
 */
export function freeInputs(cards, placed, taken = null) {
  const open = (ref, slot) => !taken?.has(slotKey(ref, slot));
  const free = [];
  for (const c of cards || []) {
    if (c.kind === "list") {
      const used = new Set((placed?.lists?.get(c.key) || []).map((w) => w.slot || 0));
      for (let n = 0; n < c.cap; n++) if (!used.has(n) && open(c.ref, n)) free.push({ ref: c.ref, slot: n, type: c.type });
    } else {
      for (const r of c.roles) if (!placed?.roles?.has(r.key) && open(r.ref, r.slot)) free.push({ ref: r.ref, slot: r.slot, type: r.type });
    }
  }
  return free;
}

/** A file's kind, by its MIME type. */
export const kindOfFile = (f) => (f?.type?.startsWith("video") ? "video" : f?.type?.startsWith("audio") ? "audio" : "image");

/**
 * Files onto free inputs, the first free of each file's kind, in the order
 * picked. → { groups: [{ ref, kind, slot, files }], left: [file] }
 * One group per input (its first slot; the rest take the next free ones, as
 * addReferenceNodes does); `left` = files there's no room for.
 */
export function planFiles(files, free, kindOf = kindOfFile) {
  const pool = [...free];
  const byRef = new Map();
  const left = [];
  for (const f of files || []) {
    const kind = kindOf(f);
    const i = pool.findIndex((x) => x.type === kind);
    if (i < 0) {
      left.push(f);
      continue;
    }
    const [spot] = pool.splice(i, 1);
    const g = byRef.get(spot.ref);
    if (g) g.files.push(f);
    else byRef.set(spot.ref, { ref: spot.ref, kind, slot: spot.slot, files: [f] });
  }
  return { groups: [...byRef.values()], left };
}

/** The catalogue rules minus the ones keyed on a mode switch — the mode decides those. */
export function taskRules(rules) {
  if (!Array.isArray(rules)) return rules;
  return rules.filter((r) => !Object.keys(r?.if || {}).some((k) => TASK_SWITCHES.has(k)));
}

const firstOf = (v) => (Array.isArray(v) ? v.find(Boolean) : v) || undefined;

/**
 * A run's inputs, made right for the mode / task — the mode switches set,
 * the frames where each provider looks for them (they read
 * `first_frame_image_url` in two-frame mode and `images_urls[0]` otherwise),
 * and nothing another mode's inputs left behind. `dto` = the generate
 * payload (model inputs by reference).
 *   keyframe  — the first frame in both places, the last one with two-frame on
 *   reference — the model's references (reference mode on with images);
 *               with none wired, or on a model that reads words only, it's
 *               words → video: no media at all
 *   a tool    — its own inputs only
 *   translate — also the language as the model names it (languageOf), and
 *               `duration` = the clip's length when `seconds` is known: it's
 *               priced per second of the clip it's given, so the credits held
 *               before the run cover the whole clip (the charge afterwards is
 *               the output's own length) — never on a model with a duration
 *               setting of its own
 * `{ seconds }`: the wired video's length (FullState reads it), optional.
 */
export function applyVideoTask(dto, task, m, opts = {}) {
  const out = taskInputs(dto, task, m, opts);
  // A run with no picture on a model whose words-only path has one mode (Hailuo 2.3: no Fast without an image).
  const words = wordsModeFor(m, out);
  if (words) out.mode = words;
  return out;
}

function taskInputs(dto, task, m, { seconds = null } = {}) {
  const out = { ...dto };
  // No model to read: send what was built (never strip media on a guess).
  if (!m) return out;
  const drop = (...refs) => refs.forEach((r) => delete out[r]);
  const setSwitch = (ref, on) => {
    if (has(m, ref)) out[ref] = on;
    else delete out[ref];
  };
  if (task === "keyframe" || task === "generate") {
    const start = firstOf(out[REFS.first]) || firstOf(out[REFS.images]);
    const end = firstOf(out[REFS.last]);
    drop(...MEDIA_REFS);
    if (start) {
      if (has(m, REFS.first)) out[REFS.first] = start;
      if (has(m, REFS.images)) out[REFS.images] = [start];
    }
    const both = !!(start && end && has(m, REFS.last));
    if (both) out[REFS.last] = end;
    setSwitch(REFS.twoFrame, both);
    setSwitch(REFS.reference, false);
    return out;
  }
  // Only the mode's / task's own media rides along.
  const cards = cardsFor(task, m);
  const keep = mediaRefsOf(cards);
  for (const r of MEDIA_REFS) if (!keep.has(r)) delete out[r];
  // A LIST wired in (an Asset Gallery, a List picking several —
  // lib/utilities) is one wire but many files: never more than the model
  // takes (its declared count when ai-back declares one — cardsFor).
  const listCap = new Map(cards.filter((c) => c.kind === "list").map((c) => [c.ref, c.cap]));
  for (const r of keep) {
    const cap = listCap.get(r) ?? capOf(m, r);
    if (cap > 0 && Array.isArray(out[r]) && out[r].length > cap) out[r] = out[r].slice(0, cap);
  }
  setSwitch(REFS.twoFrame, false);
  setSwitch(REFS.reference, (task === "reference" || task === "references") && !!firstOf(out[REFS.images]));
  if (task === "translate") {
    // ONE video: a list wired in (an Asset Gallery) gives its first — the
    // provider takes a url, and an array reaches it as one.
    if (Array.isArray(out[REFS.video])) out[REFS.video] = firstOf(out[REFS.video]);
    if (out[REFS.language] != null) out[REFS.language] = languageOf(out[REFS.language], valuesOf(m, REFS.language));
    if (Number(seconds) > 0 && !has(m, "duration")) out.duration = String(Math.max(1, Math.ceil(Number(seconds))));
  }
  return out;
}

/**
 * The mode a run with NO picture must use, when the model's words-only path
 * has just one (declared `wordsMode` — Hailuo 2.3: fal has no Fast
 * text-to-video, so a words-only run is Normal, priced as Normal). Read by the
 * run (applyVideoTask) and the price (lib/composer/quoteItem, the bar's
 * videoQuoteFill), so what's held is what runs. `values`: the run's inputs by
 * reference. → the mode, or null.
 */
export function wordsModeFor(m, values) {
  const words = declaredOf(m)?.wordsMode;
  if (!words || !values) return null;
  const picture = firstOf(values[REFS.first]) || firstOf(values[REFS.images]);
  return picture ? null : words;
}

const secs = (n) => `${Math.round(Number(n) * 10) / 10} s`;
/**
 * A run's reference videos / audio against the model's declared length limits
 * (`refVideoSeconds` / `refAudioSeconds` = { min, max, total } — Seedance 2.5:
 * each 1.8–30.2 s and 30.2 s in all), said before anything is held. `lengths`:
 * { video: [seconds|null…], audio: [seconds|null…] } — a length that couldn't
 * be read is skipped (ai-back checks again). → the problem in words, or null.
 */
export function lengthProblem(m, lengths, name = "This model") {
  const d = declaredOf(m);
  if (!d) return null;
  for (const [kind, key, noun] of [["video", "refVideoSeconds", "video"], ["audio", "refAudioSeconds", "audio clip"]]) {
    const lim = d[key];
    const known = (lengths?.[kind] || []).filter((n) => Number(n) > 0);
    if (!lim || !known.length) continue;
    const plural = `${noun}s`;
    if (lim.min && known.some((n) => n < lim.min)) return `A reference ${noun} is too short — ${name} takes ${secs(lim.min)} or more`;
    if (lim.max && known.some((n) => n > lim.max)) return `A reference ${noun} is too long — ${name} takes up to ${secs(lim.max)}`;
    const all = known.reduce((a, n) => a + n, 0);
    if (lim.total && known.length > 1 && all > lim.total) return `Reference ${plural} add up to ${secs(all)} — ${name} takes up to ${secs(lim.total)} in all`;
  }
  return null;
}

/** "An image", "A video", "Audio" — a list card's thing, for "… is needed". */
const aNoun = (noun) => (/^audio$/i.test(noun) ? "Audio" : `${/^[aeiou]/i.test(noun) ? "An" : "A"} ${noun.toLowerCase()}`);

/**
 * What the cards still need before a run: a role or list the model
 * requires, fewer references than the model's fewest (`min`), a last frame
 * without a first, audio without an image or video.
 * `wires` as for placeWires; `bound`: model inputs a variable fills.
 * → { card, label, need? } | null — `need`: the role's own words for it
 * ("Add a picture of the character"), when it has them (needText).
 */
export function taskMissing(cards, wires, bound = new Set()) {
  const { roles, lists } = placeWires(cards, wires || []);
  const roleFilled = (r) => roles.has(r.key) || bound.has(r.ref);
  const listFilled = (c) => (lists.get(c.key) || []).length > 0 || bound.has(c.ref);
  for (const c of cards) {
    if (c.kind === "list") {
      if (c.required && !listFilled(c)) return { card: c.key, label: aNoun(c.noun) };
      // Fewer than the provider's fewest (Seedance 2 on KIE: 2–9 references), when any are in.
      const n = (lists.get(c.key) || []).length;
      if (c.min && n > 0 && n < c.min && !bound.has(c.ref)) return { card: c.key, label: c.min - n === 1 ? `Another ${c.noun.toLowerCase()}` : `${c.min - n} more ${c.noun.toLowerCase()}s` };
      if (c.needsAny && listFilled(c) && !cards.some((o) => c.needsAny.includes(o.key) && o.kind === "list" && listFilled(o))) return { card: c.key, label: "An image or a video" };
      continue;
    }
    for (const r of c.roles) {
      if (r.required && !roleFilled(r)) return { card: c.key, label: r.label, ...(r.need ? { need: r.need } : {}) };
      if (r.needs && roleFilled(r)) {
        const need = c.roles.find((x) => x.key === r.needs);
        if (need && !roleFilled(need)) return { card: c.key, label: need.label, ...(need.need ? { need: need.need } : {}) };
      }
    }
  }
  return null;
}

/**
 * The line for something a run still needs: its own words when it has them
 * (a role's `need` — "Add a video of the moves"), else "<name> is needed".
 * `missing`: { name | label, need? } (useComposerState's `missing`, or
 * taskMissing's answer). → the words, or null.
 */
export function needText(missing) {
  if (!missing) return null;
  if (missing.need) return missing.need;
  const name = missing.name ?? missing.label;
  return name ? `${name} is needed` : null;
}

/**
 * A model whose clip runs as long as the one it's given — declared by ai-back
 * (`videoInputs.outputSecondsFromInput`: a motion transfer, a reframe, a
 * talking avatar's voice), and every Motion transfer and Translate video
 * (their output is their input's length, whatever the row says). Such a model
 * is priced per second of that clip (sourcePrice).
 */
export function followsSource(m, task = null) {
  return declaredOf(m)?.outputSecondsFromInput === true || task === "motion" || task === "translate";
}

/**
 * The seconds such a model makes from a clip of `seconds`: all of them, or no
 * more than it declares (`videoInputs.outputSecondsMax` — Runway Aleph uses
 * only the first 5 s of its video, 3 Oct 2026). null while unread.
 */
export function sourceSecondsOf(m, seconds) {
  const n = Number(seconds);
  if (!(n > 0)) return seconds ?? null;
  const max = Number(declaredOf(m)?.outputSecondsMax);
  return max > 0 ? Math.min(n, max) : n;
}

/**
 * What a run of a source-length model costs, in Generate's words (Tarek,
 * 3 Oct 2026: "the clip is as long as the Moves video"):
 *   no clip wired yet   → "135 credits a second"
 *   the clip's length   → "6 s · 810 credits" (the per-second price × the
 *                         clip's seconds, rounded up)
 * The clip's length is read in the browser (the node and the bar read the
 * wired file); the price of a second is the quote's (`totalCredits` — it
 * follows the node's settings). A quote without a clip prices 5 s, so its
 * total is never shown before one is wired (that was the 675 = 5 × 135). A
 * clip the browser can't read: the quote's total (`credits` — ai-back
 * measures the source itself, 3 Oct 2026), its seconds when they divide.
 * `wired`: a clip is wired in; `seconds`: its length (null while unread);
 * `perSecond`: the price of one second; `credits`: the quote's total.
 * → { words, credits } | null (nothing to say yet — Generate shows no price)
 */
export function sourcePrice({ wired, seconds, perSecond, credits = null }) {
  const rate = Number(perSecond) > 0 ? Math.round(Number(perSecond)) : null;
  if (!rate) return null;
  if (!wired) return { words: `${formatCredits(rate)} a second`, credits: rate };
  const read = Number(seconds) > 0 ? Math.ceil(Number(seconds)) : null;
  const quoted = Number(credits) > 0 ? Math.round(Number(credits)) : null;
  if (!read && !quoted) return null;
  if (!read) {
    const secs = quoted % rate === 0 ? quoted / rate : null;
    return { words: secs ? `${secs} s · ${formatCredits(quoted)}` : `${formatCredits(quoted)}`, credits: quoted };
  }
  const total = rate * read;
  return { words: `${read} s · ${formatCredits(total)}`, credits: total };
}

const WIRE_KIND = { [REFS.video]: "video", [REFS.audio]: "audio" };
/**
 * How many of `wires` a switch to `cards` leaves with nowhere to go — the
 * ones the new cards don't place and no free input of their kind takes (the
 * move the node makes on a switch). `wires`: [{ ref, slot, type? }]
 */
export function leftAfterSwitch(cards, wires) {
  const real = (wires || []).filter((w) => MEDIA_REFS.includes(w.ref)).map((w) => ({ ...w, type: w.type || WIRE_KIND[w.ref] || "image" }));
  const placed = placeWires(cards, real);
  const free = freeInputs(cards, placed);
  let left = 0;
  for (const w of placed.off) {
    const i = free.findIndex((f) => f.type === w.type);
    if (i < 0) left += 1;
    else free.splice(i, 1);
  }
  return left;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
/**
 * The words a switch would cost, for the side not in use ("2 references
 * won’t be used") — "" when nothing would be left out. `rules` = sideRules(…).
 * → { keyframe, reference }
 */
export function switchLeaves(task, rules, wires) {
  const out = { keyframe: "", reference: "" };
  if (task !== "keyframe" && rules.keyframe.ok) {
    const n = leftAfterSwitch(cardsFor("keyframe", rules.keyframe.model), wires);
    if (n) out.keyframe = `${plural(n, "reference", "references")} won’t be used`;
  }
  if (task !== "reference" && rules.reference.ok) {
    const n = leftAfterSwitch(cardsFor("reference", rules.reference.model), wires);
    if (n) out.reference = `${plural(n, "frame", "frames")} won’t be used`;
  }
  return out;
}
